import { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { reviewTokens } from "../../theme";

export interface ReviewEmptyStateProps {
  title: string;
  subtitle?: string;
  /** Icon shown in the circular badge. Defaults to an inbox icon. */
  icon?: ReactNode;
  /**
   * `card` (default) renders the bordered panel used for whole-region empties;
   * `inline` renders a compact centered stack for empties inside a panel.
   */
  variant?: "card" | "inline";
  testId?: string;
}

/**
 * Shared empty-state block for the Flyer Review feature. Generalized from the
 * original `ChangesByItemEmptyState` so the same look is reused across the nav
 * list, workbench, and comments without duplicating markup.
 */
export function ReviewEmptyState({
  title,
  subtitle,
  icon,
  variant = "card",
  testId,
}: ReviewEmptyStateProps) {
  const content = (
    <Stack spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: reviewTokens.slate100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon ?? (
          <InboxOutlinedIcon sx={{ fontSize: 28, color: "text.secondary" }} />
        )}
      </Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ maxWidth: 360 }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );

  if (variant === "inline") {
    return (
      <Box data-testid={testId} sx={{ py: 6 }}>
        {content}
      </Box>
    );
  }

  return (
    <Box
      data-testid={testId}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: `${reviewTokens.radiusLg}px`,
        bgcolor: "background.paper",
        py: 8,
        px: 3,
      }}
    >
      {content}
    </Box>
  );
}
